import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getGraphQLErrorMessage } from 'common/utils/i18nErrorTranslations';
import { deleteManufacturer } from 'store/thunks/manufacturerManagementThunk';

const RemoveManufacturerDialog = ({ open, manufacturer, onClose }) => {
  const dispatch = useDispatch();
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  // Kept through the closing fade, after the parent has cleared `manufacturer`.
  const [target, setTarget] = useState(manufacturer);

  useEffect(() => {
    if (open) {
      setError('');
      setTarget(manufacturer);
    }
  }, [open, manufacturer]);

  const handleRemove = () => {
    setRemoving(true);
    setError('');

    dispatch(deleteManufacturer({ manufacturerId: target?.manufacturerId }))
      .then(() => onClose())
      .catch((removeError) => setError(getGraphQLErrorMessage(removeError)))
      .finally(() => setRemoving(false));
  };

  return (
    <Dialog open={open} onClose={removing ? undefined : onClose} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ fontWeight: 700 }}>Remove manufacturer?</DialogTitle>
      <DialogContent>
        <Typography variant='body2'>
          Are you sure you want to remove <strong>{target?.manufacturer}</strong>?
        </Typography>
        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={removing}>
          Cancel
        </Button>
        {/* Shows only the spinner while removing; the label is hidden but keeps the width. */}
        <LoadingButton variant='contained' color='error' onClick={handleRemove} loading={removing}>
          Remove
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

RemoveManufacturerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  manufacturer: PropTypes.shape({
    manufacturerId: PropTypes.string.isRequired,
    manufacturer: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
};

RemoveManufacturerDialog.defaultProps = {
  manufacturer: null,
};

export default RemoveManufacturerDialog;
